import {useSpeculativeTyping} from '../hooks/useSpeculativeTyping';
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useAuth } from '../components/AuthProvider';
import api from '../utils/api';
import { connectToWebSocket, sendMessage as wsSendMessage, addMessageHandler, removeMessageHandler, disconnect, isConnected, getConnectionInfo, getHandlerCount, clearAllHandlers } from '../utils/websocket';
import { debugWS, warnWS, errorWS, logWSLifecycle, logWSMessage } from '../utils/debugWS';
import { WS_BACKEND_URL, BACKEND_URL } from '../config';
import MobileInviteButton from './components/MobileInviteButton';
import { useTypingIndicators } from '../hooks/useTypingIndicators';
import '../components/TypingIndicator.css';

// Helper function to extract display name from user object
const getDisplayNameFromUser = (user) => {
  if (!user) return 'Guest';
  
  return (
    user.user_metadata?.full_name ||
    user.user_metadata?.name ||
    user.user_metadata?.username ||
    user.name ||
    user.username ||
    user.email?.split('@')[0]?.replace('.', ' ').replace('_', ' ').split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ') ||
    'Guest'
  );
};

/**
 * Mobile-optimized ChatRoom component
 * Focused on messaging with collapsible context panels
 */
const MobileChatRoom = ({ room, user, onLeave, onSignIn }) => {
  // Guard: Ensure required room parameter exists
  if (!room || !room.id) {
    console.warn('MobileChatRoom: Missing required room parameter');
    return null;
  }

  // Get user display name
  const userDisplayName = useMemo(() => {
    // When in a guest room, always use the guest identity from sessionStorage,
    // even if the user is also logged in (e.g. joining a Daily invite while authenticated).
    if (room.isGuest) {
      const storedGuestUser = sessionStorage.getItem('guest_user');
      if (storedGuestUser) {
        try {
          const guestUser = JSON.parse(storedGuestUser);
          return guestUser.display_name;
        } catch (error) {
          console.warn('Error parsing guest user data for display name:', error);
          return 'Guest';
        }
      }
      return 'Guest';
    }
    if (user) {
      return getDisplayNameFromUser(user);
    }
    return 'Guest';
  }, [user, room.isGuest]);

  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [connected, setConnected] = useState(false);
  const [error, setError] = useState('');
  const [sceneInfo, setSceneInfo] = useState(null);
  const [sceneLoading, setSceneLoading] = useState(false);
  
  // Mobile-specific state
  const [showInfoModal, setShowInfoModal] = useState(false);
  
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);
  const debugIdRef = useRef(Date.now());
  const { session } = useAuth();
  const seenMessagesRef = useRef(new Set());
  const handlerRegisteredRef = useRef(false);
  const initialConnectionRef = useRef(true);

  // Typing indicators state (managed internally)
  const [typingActiveCount, setTypingActiveCount] = useState(0);
  const [typingIsAnyoneTyping, setTypingIsAnyoneTyping] = useState(false);
  const [sceneTransitioning, setSceneTransitioning] = useState(false);
  const [responseDraftsAvailable, setResponseDraftsAvailable] = useState(false);
  const {enabled: responseDraftsEnabled,
    setEnabled: setResponseDraftsEnabled, beforeSubmit: stopDraftTimer} =
    useSpeculativeTyping(room?.id, newMessage, connected && !sceneTransitioning, responseDraftsAvailable);

  
  // Typing indicators integration
  const {
    isCurrentUserTyping,
    handleKeyDown: handleTypingKeyDown,
    handleMessageSend: handleTypingMessageSend,
    sendTypingStop
  } = useTypingIndicators(room?.id);

  // Auto-scroll to bottom when messages change - with better timing and positioning
  useEffect(() => {
    const scrollToBottom = () => {
      if (messagesEndRef.current) {
        const container = messagesEndRef.current.closest('.overflow-y-auto');
        if (container) {
          // Scroll to the very bottom plus a bit extra to account for input area
          container.scrollTop = container.scrollHeight + 100;
        }
      }
    };

    // Use requestAnimationFrame to ensure DOM is updated
    const timeoutId = setTimeout(() => {
      requestAnimationFrame(scrollToBottom);
    }, 50);

    return () => clearTimeout(timeoutId);
  }, [messages]);

  // Handle textarea height - account for padding and spacing
  const textareaHeight = 80; // Reduced from 90px

  // WebSocket message handler
  // C-proto: data is typed as ServerWSEvent (see frontend/src/proto/interfaces.ts)
  const handleWebSocketMessage = useCallback(/** @param {import('../proto/interfaces').ServerWSEvent} data */ (data) => {
    logWSMessage('📨 MOBILE CHATROOM HANDLER:', JSON.stringify(data, null, 2));
    
    if (data.type === 'new_message') {
      const messageId = data.id;
      
      // Scene transition detection
      if (data.sender_name === 'Narrator' && data.metadata) {
        if (data.metadata.update_scene_indicator && data.metadata.scene_sequence) {
          // Narrator opening beat arrived — clear the transitioning indicator.
          setSceneTransitioning(false);
          // Re-fetch full scene info (including activeCharacterIds) from the backend.
          setTimeout(() => loadSceneInfo(), 500);
        }
      }
      
      // Skip invalid messages
      if (!messageId) {
        logWSMessage('📨 MOBILE: Skipping message with missing ID');
        return;
      }
      
      // Anti-echo protection
      const isFromCurrentUser = userDisplayName && data.sender_name === userDisplayName;
      let isFromCurrentGuestUser = false;

      if (!user || room.isGuest) {
        const storedGuestUser = sessionStorage.getItem('guest_user');
        if (storedGuestUser) {
          try {
            const guestUser = JSON.parse(storedGuestUser);
            isFromCurrentGuestUser = data.sender_name === guestUser.display_name;
          } catch (error) {
            logWSMessage('📨 MOBILE: Error parsing guest user data:', error);
          }
        }
      }
      
      const isAIMessage = data.message_type === 'ai' || data.sender_type === 'ai_response' || data.sender_type === 'ai';
      
      if ((isFromCurrentUser || isFromCurrentGuestUser) && !isAIMessage) {
        logWSMessage('📨 MOBILE: Skipping echo message from current user');
        return;
      }
      
      // Check for duplicates
      const messageSignature = `${messageId}-${data.content}-${data.sender_name}-${data.created_at}`;
      if (seenMessagesRef.current.has(messageId) || seenMessagesRef.current.has(messageSignature)) {
        logWSMessage('📨 MOBILE: Skipping duplicate message');
        return;
      }
      
      // Add message to state
      setMessages(prev => {
        const messageExists = prev.some(msg => 
          msg.id === data.id || 
          (msg.content === data.content && msg.sender_name === data.sender_name)
        );
        
        if (messageExists) {
          return prev;
        }

        // Remove optimistic messages
        const filteredMessages = prev.filter(msg => 
          !(msg.isOptimistic && msg.content === data.content && msg.sender_name === data.sender_name)
        );

        const newMessage = {
          id: data.id,
          content: data.content,
          sender_name: data.sender_name,
          message_type: data.message_type,
          created_at: data.created_at,
          room_id: data.room_id
        };

        return [...filteredMessages, newMessage];
      });
      
      // Mark as seen for user messages only
      const isUserMessage = data.message_type === 'user' || data.sender_type === 'user';
      if (isUserMessage) {
        seenMessagesRef.current.add(messageId);
        seenMessagesRef.current.add(messageSignature);
      }
      
    } else if (data.type === 'connection_status') {
      setConnected(data.connected);
      if (data.connected) {
        initialConnectionRef.current = false;
        setError('');
      } else if (!initialConnectionRef.current) {
        setError('Disconnected');
      }
    } else if (data.type === 'connection_error') {
      if (!initialConnectionRef.current) {
        setError(data.error || 'Connection error');
      }
      setConnected(false);
    } else if (data.type === 'connection_confirmed') {
      setResponseDraftsAvailable(data.response_drafts_enabled === true);
      setConnected(true);
      initialConnectionRef.current = false;
      setError('');
    } else if (data.type === 'error') {
      setError(data.message || 'Story generation is temporarily unavailable.');
      setTypingActiveCount(0);
      setTypingIsAnyoneTyping(false);
      setSceneTransitioning(false);
    } else if (data.type === 'turn_dropped') {
      setMessages(prev => [...prev, {
        id: `turn_dropped_${Date.now()}`,
        sender_name: 'Narrator',
        sender_type: 'narrator',
        message_type: 'message',
        content: '[Sorry, an error occurred. Please resend your message and the story will continue.]',
        created_at: new Date().toISOString(),
        timestamp: new Date().toISOString(),
      }]);
    } else if (data.type === 'typing_status') {
      // Handle typing status directly in main handler
      console.log(`⌨️ MOBILE TYPING STATUS: 📥 Received for room ${room?.id}:`, data);
      
      if (data.room_id === room?.id) {
        const count = data.active_count || 0;
        const isTyping = data.is_typing || false;
        
        setTypingActiveCount(count);
        setTypingIsAnyoneTyping(isTyping);
        
        console.log(`⌨️ MOBILE TYPING STATUS: Updated state - count=${count}, isTyping=${isTyping}`);
        
        if (count > 0) {
          console.log(`⌨️ MOBILE TYPING INDICATOR SHOULD BE VISIBLE: "${data.message}" with count ${count}`);
        } else {
          console.log(`⌨️ MOBILE TYPING INDICATOR SHOULD BE HIDDEN: count is 0`);
        }
      } else {
        console.log(`⌨️ MOBILE TYPING STATUS: Ignoring for different room - data: ${data.room_id}, current: ${room?.id}`);
      }
    } else if (data.type === 'narrator_transitioning') {
      setSceneTransitioning(data.active === true);
    }
  }, [setMessages, setConnected, setError, user, userDisplayName, setSceneInfo, setSceneTransitioning]);

  // Load messages
  const loadMessages = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      
      let messagesUrl = `/messages/${room.id}?limit=200`;
      try {
        const stored = sessionStorage.getItem('guest_user');
        if (stored) {
          const guestUser = JSON.parse(stored);
          if (guestUser?.id) messagesUrl += `&guest_id=${guestUser.id}`;
        }
      } catch (_) {}
      const response = await api.get(messagesUrl);

      if (!response.messages || response.messages.length === 0) {
        setMessages(prev => prev.length > 0 ? prev : []);
      } else {
        setMessages(response.messages);
        
        // Pre-populate seen messages cache for user messages only
        seenMessagesRef.current.clear();
        response.messages.forEach(msg => {
          if (msg.message_type === 'user' || msg.sender_type === 'user') {
            seenMessagesRef.current.add(msg.id);
            const contentSignature = `${msg.content}-${msg.sender_name}`;
            seenMessagesRef.current.add(contentSignature);
          }
        });
      }
    } catch (error) {
      console.error('MOBILE: Error loading messages:', error);
      setMessages([]);
      seenMessagesRef.current.clear();
      if (error.message && !error.message.includes('Network Error')) {
        setError('Failed to load message history');
      }
    } finally {
      setLoading(false);
    }
  }, [room?.id]);

  // Load scene info
  const loadSceneInfo = useCallback(async () => {
    if (!room?.id) return;
    
    try {
      setSceneLoading(true);
      const response = await api.get(`/scenes/validate/${room.id}`);
      
      if (response.ready && response.scene) {
        const sceneData = response.scene.state_data;
        setSceneInfo({
          sequence: sceneData.scene_sequence,
          title: sceneData.scene_title,
          activeCharacterIds: sceneData.active_character_ids || [],
          setting: sceneData.setting_description,
          mood: sceneData.mood
        });
      }
      // When !ready, preserve existing sceneInfo — the WebSocket handler will
      // update it when the new scene's Narrator message arrives.
    } catch (error) {
      console.error('MOBILE: Error loading scene info:', error);
    } finally {
      setSceneLoading(false);
    }
  }, [room?.id]);

  useEffect(() => {
    loadSceneInfo();
  }, [loadSceneInfo]);

  // Set up WebSocket connection
  useEffect(() => {
    debugWS('🚨 MOBILE ChatRoom useEffect MOUNT - setting up handlers');
    
    clearAllHandlers();
    addMessageHandler(handleWebSocketMessage);
    handlerRegisteredRef.current = true;
    
    return () => {
      debugWS('💀 MOBILE ChatRoom useEffect UNMOUNT - cleaning up handlers');
      if (handlerRegisteredRef.current) {
        clearAllHandlers();
        handlerRegisteredRef.current = false;
        seenMessagesRef.current.clear();
        initialConnectionRef.current = true;
        disconnect();
      }
    };
  }, []);

  // Connection effect
  useEffect(() => {
    if (!room?.id) return;

    if (room.isGuest) {
      const storedGuestUser = sessionStorage.getItem('guest_user');
      if (storedGuestUser) {
        try {
          const guestUser = JSON.parse(storedGuestUser);
          const guestSession = { 
            access_token: `guest_${guestUser.id}`, 
            session_id: 'guest_session' 
          };
          
          initialConnectionRef.current = true;
          loadMessages();
          connectToWebSocket(room.id, guestUser, guestSession);
          setConnected(isConnected());
          
          return () => {
            disconnect();
          };
        } catch (error) {
          debugWS('❌ MOBILE: Error parsing stored guest user data:', error);
        }
      }
      return;
    }

    if (!user || !session?.access_token) return;

    initialConnectionRef.current = true;
    loadMessages();
    connectToWebSocket(room.id, user, session);
    setConnected(isConnected());
    
    return () => {
      disconnect();
    };
    
  }, [room?.id, user?.id, session?.access_token, loadMessages]);

  // Update connection status periodically
  useEffect(() => {
    const interval = setInterval(() => {
      const wsInfo = getConnectionInfo();
      setConnected(wsInfo.connected);
    }, 5000);
    
    return () => clearInterval(interval);
  }, []);

  // Send message function
  const sendMessage = useCallback(async (content) => {
    if (!content || !content.trim()) {
      setError('Message cannot be empty');
      return;
    }

    if (content.length > 2000) {
      setError('Message too long (maximum 2000 characters)');
      return;
    }

    if (sending) return;

    try {
      setSending(true);
      setError('');

      // Optimistic update
      const optimisticMessage = {
        id: `temp-${Date.now()}-${Math.random()}`,
        content: content,
        sender_name: userDisplayName,
        message_type: 'user',
        created_at: new Date().toISOString(),
        room_id: room?.id,
        isOptimistic: true
      };

      setMessages(prev => [...prev, optimisticMessage]);

      // 🔧 STEP 1: Clear typing state immediately on message send (before WebSocket call)
      // This prevents the flash from buffered typing events
      setTypingActiveCount(0);
      setTypingIsAnyoneTyping(false);

      stopDraftTimer();
      const success = await wsSendMessage(content, api);
      
      if (success) {
        // Stop typing indicator when message is sent successfully
        handleTypingMessageSend();
        
        setNewMessage('');
        
        // Auto-focus the textarea for seamless conversation flow
        // Use timeout to ensure focus happens after all state updates
        setTimeout(() => {
          if (textareaRef.current) {
            textareaRef.current.focus();
          }
        }, 50);
        
        setMessages(prev => prev.map(msg => 
          msg.id === optimisticMessage.id 
            ? { ...msg, isOptimistic: false }
            : msg
        ));
        
        // Force scroll after sending message
        setTimeout(() => {
          if (messagesEndRef.current) {
            const container = messagesEndRef.current.closest('.overflow-y-auto');
            if (container) {
              container.scrollTop = container.scrollHeight + 100;
            }
          }
        }, 100);
      } else {
        setMessages(prev => prev.filter(msg => msg.id !== optimisticMessage.id));
        setError('Failed to send message. Please try again.');
      }

    } catch (error) {
      console.error('MOBILE: Error sending message:', error);
      
      if (error.message.includes('rate limit')) {
        setError('Sending too fast. Please wait a moment.');
      } else if (error.message.includes('too long')) {
        setError('Message is too long. Please shorten your message.');
      } else {
        setError('Failed to send message. Please try again.');
      }
    } finally {
      setSending(false);
    }
  }, [connected, sending, userDisplayName, room?.id, stopDraftTimer]);

  const handleSendClick = useCallback(() => {
    sendMessage(newMessage);
  }, [sendMessage, newMessage]);

  const handleKeyPress = useCallback((e) => {
    if (e.key === 'Enter' && !e.shiftKey && !sceneTransitioning) {
      e.preventDefault();
      handleSendClick();
    }
  }, [handleSendClick, sceneTransitioning]);

  const formatTime = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString([], { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  return (
    <div className="h-screen bg-gray-900 flex flex-col overflow-hidden max-w-full" style={{ position: 'relative', top: 0 }}>
      {/* Mobile Header - Fixed at top */}
      <div className="fixed top-0 left-0 right-0 bg-gray-800 border-b border-gray-700 p-3 w-full max-w-full z-50" style={{ minHeight: '60px' }}>
        <div className="flex items-center justify-between">
          {/* Back Button & Clickable Title */}
          <div className="flex items-center space-x-3 flex-1 min-w-0">
            <button
              onClick={onLeave}
              className="p-2 hover:bg-gray-700 rounded-lg transition-colors flex-shrink-0"
            >
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            
            <button
              onClick={() => setShowInfoModal(true)}
              className="flex-1 min-w-0 text-left hover:bg-gray-700 rounded-lg p-2 -m-2 transition-colors"
            >
              <h1 className="text-white font-medium truncate text-sm">
                {room.narrative?.title || room.name}
              </h1>
              <div className="flex items-center space-x-2">
                <p className="text-gray-400 text-xs truncate">
                  {room.narrative?.genre}
                </p>
                {sceneInfo && (
                  <span className="text-xs text-blue-400">
                    Scene {sceneInfo.sequence}
                  </span>
                )}
              </div>
            </button>
          </div>

          {/* Right Side Controls */}
          <div className="flex items-center space-x-2">
            {/* Connection Status */}
            <div className="flex items-center">
              {connected ? (
                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
              ) : (
                <div className="w-2 h-2 bg-red-500 rounded-full"></div>
              )}
            </div>

            {!user && (
              <button
                onClick={onSignIn}
                className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded text-xs font-medium"
              >
                Sign In
              </button>
            )}
            
            {/* Always Visible Invite Button */}
            {user && (
              <div className="flex-shrink-0">
                <MobileInviteButton isAuthenticated={!!user} onSignIn={onSignIn} roomId={room?.id} />
              </div>
            )}
          </div>
        </div>

        {/* Info Modal - Scene Info + Story Context */}
        {showInfoModal && (
          <div 
            className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={() => setShowInfoModal(false)}
          >
            <div 
              className="bg-gray-800 rounded-lg max-w-sm w-full max-h-[80vh] overflow-hidden shadow-xl border border-gray-700"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="bg-gray-700 p-4 border-b border-gray-600 flex items-center justify-between">
                <h2 className="text-white font-medium">
                  {room.narrative?.title || room.name}
                </h2>
                <button
                  onClick={() => setShowInfoModal(false)}
                  className="text-gray-400 hover:text-white p-1"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              {/* Modal Content */}
              <div className="p-4 overflow-y-auto max-h-[calc(80vh-100px)]">
                {/* Scene Info Section */}
                {sceneInfo && (
                  <div className="mb-4">
                    <h3 className="text-white font-medium text-sm mb-2 flex items-center">
                      <svg className="w-4 h-4 mr-2 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 4V2a1 1 0 011-1h8a1 1 0 011 1v2m-9 0h10l1 13H5L6 4z" />
                      </svg>
                      Current Scene
                    </h3>
                    <div className="bg-gray-700 rounded-lg p-3">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-blue-400 font-medium text-sm">Scene {sceneInfo.sequence}</span>
                        <span className="text-gray-400 text-xs">
                          {sceneInfo.activeCharacterIds.length} active character{sceneInfo.activeCharacterIds.length !== 1 ? 's' : ''}
                        </span>
                      </div>
                      {sceneInfo.title && (
                        <p className="text-gray-300 text-sm">{sceneInfo.title}</p>
                      )}
                      {sceneInfo.setting && (
                        <p className="text-gray-400 text-xs mt-1">{sceneInfo.setting}</p>
                      )}
                    </div>
                  </div>
                )}
                
                {/* Story Context Section */}
                {room.narrative && (
                  <div>
                    <h3 className="text-white font-medium text-sm mb-2 flex items-center">
                      <svg className="w-4 h-4 mr-2 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                      </svg>
                      Story Background
                    </h3>
                    <div className="bg-gray-700 rounded-lg p-3">
                      <div className="flex items-center mb-2">
                        <span className="bg-gray-600 text-gray-300 px-2 py-1 rounded text-xs font-medium">
                          {room.narrative.genre}
                        </span>
                        {room.narrative.featured_character && (
                          <span className="text-gray-400 text-xs ml-2">
                            🎭 {room.narrative.featured_character}
                          </span>
                        )}
                      </div>
                      <p className="text-gray-300 text-sm leading-relaxed">
                        {room.narrative.description}
                      </p>
                    </div>
                  </div>
                )}
              </div>
              
              {/* Modal Footer */}
              <div className="bg-gray-700 p-3 border-t border-gray-600 text-center">
                <button
                  onClick={() => setShowInfoModal(false)}
                  className="text-gray-400 text-xs hover:text-white"
                >
                  Tap outside or here to close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Messages Area */}
      <div 
        className="flex-1 overflow-y-auto p-3 w-full max-w-full"
        style={{ 
          marginBottom: `${textareaHeight + 5}px`, // Reduced buffer from 20px to 5px
          marginTop: '75px', // Space for fixed header
          paddingBottom: '12px' // Reduced from 24px to 12px
        }}
      >
        {loading ? (
          <div className="flex items-center justify-center h-32">
            <div className="text-gray-400">Loading messages...</div>
          </div>
        ) : messages.length === 0 ? (
          <div className="flex items-center justify-center h-32">
            <div className="text-center text-gray-400">
              <p className="text-lg">Welcome to the story!</p>
              <p className="text-sm mt-2">Start the conversation and let the adventure begin...</p>
            </div>
          </div>
        ) : (
          <div className="space-y-3 max-w-full">
            {messages.map((message) => {
              // Check if this message is from the current user
              let isCurrentUser = false;
              
              if (user) {
                isCurrentUser = message.sender_name === userDisplayName;
              } else {
                const storedGuestUser = sessionStorage.getItem('guest_user');
                if (storedGuestUser) {
                  try {
                    const guestUser = JSON.parse(storedGuestUser);
                    isCurrentUser = message.sender_name === guestUser.display_name;
                  } catch (error) {
                    console.warn('Error parsing guest user data for message comparison:', error);
                  }
                }
              }
              
              return (
                <div
                  key={message.id || `msg-${message.created_at || Date.now()}-${Math.random()}`}
                  className={`flex w-full ${isCurrentUser ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[75%] px-3 py-2 rounded-2xl ${
                      message.message_type === 'ai'
                        ? 'bg-gray-700 text-white' // AI characters
                        : isCurrentUser
                        ? 'bg-blue-600 text-white' // Current user
                        : 'bg-gray-600 text-white'  // Other users
                    }`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs font-medium opacity-75">
                        {message.sender_name || 'Unknown'}
                      </span>
                      <span className="text-xs opacity-50 ml-2">
                        {formatTime(message.created_at)}
                      </span>
                    </div>
                    <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                  </div>
                </div>
              );
            })}
            
            <div ref={messagesEndRef} className="h-8 w-full" />
          </div>
        )}
      </div>

      {/* Fixed Input Area */}
      <div 
        className="fixed bottom-0 left-0 right-0 bg-gray-800 border-t border-gray-700 w-full max-w-full shadow-lg z-40"
        style={{ 
          padding: '12px',
          minHeight: `${textareaHeight}px`
        }}
      >
        {error && (
          <div className="bg-red-500/20 border border-red-500 text-red-400 px-3 py-1 rounded mb-2 text-xs">
            {error}
          </div>
        )}
        
        {responseDraftsAvailable && <label className="block text-xs text-gray-300 mb-2">
          <input type="checkbox" checked={responseDraftsEnabled}
            onChange={event => setResponseDraftsEnabled(event.target.checked)} />{' '}
          Prepare replies while I type
          <span className="block">AI processes this draft before Send. Unsent text is temporary and is not posted or saved.</span>
        </label>}
        <div className="flex items-end space-x-2 w-full max-w-full">
          <div className="relative flex-1">
            <textarea
              ref={textareaRef}
              className="w-full bg-gray-700 text-white placeholder-gray-400 rounded-2xl px-3 py-2 resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              placeholder={
                sceneTransitioning
                  ? ''
                  : (!newMessage || newMessage.trim().length === 0) && !isCurrentUserTyping && typingIsAnyoneTyping
                    ? '' // Empty placeholder when showing custom typing indicator
                    : 'Type a message...'
              }
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              onKeyDown={handleTypingKeyDown}
              disabled={sending || sceneTransitioning}
              rows={1}
              enterKeyHint="enter"
              autoComplete="on"
              autoCorrect="on"
              autoCapitalize="sentences"
              spellCheck="true"
              style={{
                minHeight: '44px',
                maxHeight: '120px'
              }}
            />
            {/* Narrator is writing indicator — shown during scene transitions */}
            {sceneTransitioning && (
              <div className="absolute inset-3 pointer-events-none text-gray-400 text-sm" style={{
                fontSize: 'inherit',
                fontFamily: 'inherit',
                lineHeight: 'inherit'
              }}>
                <span>Narrator is writing</span>
                <span className="typing-dots ml-1"><span className="dot bouncing-dot dot-1">.</span><span className="dot bouncing-dot dot-2">.</span><span className="dot bouncing-dot dot-3">.</span></span>
              </div>
            )}
            {/* Custom typing indicator that matches textarea placeholder exactly */}
            {!sceneTransitioning && (!newMessage || newMessage.trim().length === 0) && !isCurrentUserTyping && typingIsAnyoneTyping && (
              <div className="absolute inset-3 pointer-events-none text-gray-400 text-sm" style={{
                fontSize: 'inherit',
                fontFamily: 'inherit',
                lineHeight: 'inherit'
              }}>
                <span>Someone is typing</span>
                <span className="typing-dots ml-1"><span className="dot bouncing-dot dot-1">.</span><span className="dot bouncing-dot dot-2">.</span><span className="dot bouncing-dot dot-3">.</span></span>
                <span className="ml-1">({typingActiveCount})</span>
              </div>
            )}
          </div>
          <button
            onClick={handleSendClick}
            disabled={sceneTransitioning || sending || !newMessage.trim()}
            className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 text-white rounded-full flex-shrink-0 transition-colors flex items-center justify-center"
            style={{ 
              minWidth: '44px', 
              width: '44px', 
              height: '44px',
              padding: '0'
            }}
          >
            {sending ? (
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
              </svg>
            )}
          </button>
        </div>
        
        {/* Character Counter */}
        <div className="flex justify-between items-center mt-1 px-1">
          <span className="text-xs text-gray-400">
            {newMessage.length}/2000
          </span>
          {newMessage.length > 1800 && (
            <span className="text-xs text-yellow-400">
              {newMessage.length > 1950 ? 'Almost at limit!' : 'Approaching limit'}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default MobileChatRoom;